import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import type { VaultV1Migration } from '@/lib/procedures/vaultV1Migration.contract';
import type { V1ToV2MigrationEntrySource } from '../events';
import { trackV1ToV2MigrationEntryViewed } from '../trackers/migration';

/**
 * Fires one `v1_to_v2_migration_entry_viewed` impression when the upgrade entry
 * point (banner or dashboard button) mounts. `source` distinguishes the surface
 * so views can be compared per entry point and against clicks.
 *
 * An Effect is the right tool: an impression has no user event to hang it on —
 * the only signal is the component being committed — and firing in render would
 * break purity (these components run under the React Compiler). The ref
 * keeps it to one fire per mount, including dev StrictMode's double-invoke. The
 * entry only mounts when a `target` exists, so no loading gate is needed.
 */
export const useTrackV1ToV2MigrationEntryViewed = (source, target) => {
  const $ = _c(6);
  const tracked = useRef(false);
  let t0;
  let t1;
  if ($[0] !== source || $[1] !== target.chainId || $[2] !== target.v1VaultAddress || $[3] !== target.v2VaultAddress) {
    t0 = () => {
      if (tracked.current) {
        return;
      }
      tracked.current = true;
      trackV1ToV2MigrationEntryViewed({
        source,
        chainId: target.chainId,
        v1VaultAddress: target.v1VaultAddress,
        v2VaultAddress: target.v2VaultAddress
      });
    };
    t1 = [source, target.chainId, target.v1VaultAddress, target.v2VaultAddress];
    $[0] = source;
    $[1] = target.chainId;
    $[2] = target.v1VaultAddress;
    $[3] = target.v2VaultAddress;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
};